import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ApiService } from '../api.service';
import {
  ArchivedNewsApiResponse,
  CreateNewsResponse,
  NewsActionResponse,
  NewsManagementApiResponse,
  NewsManagementArchivedItem,
  NewsItem,
  NewsManagementItem,
  NewsResponse,
  UpdateNewsMetadataPayload,
  UpdateVisibilityPayload,
} from '../../interfaces/news';
import { unwrapApiEnvelope } from '../../shared/data/api-envelope';

@Injectable({
  providedIn: 'root',
})
export class NewsService {
  constructor(private apiService: ApiService) {}

  /**
   * Get all active news items visible for user's org
   *
   * Returns news items as a domain type rather than the raw envelope — matching
   * getAllActiveNews() below, which already unwrapped.
   */
  getNews(): Observable<NewsItem[]> {
    return this.apiService.get<NewsResponse>(`/v1/news`).pipe(
      unwrapApiEnvelope({ array: true, fallbackMessage: $localize`The news response was not in the expected format.` }),
      map((data) =>
        data.map((item) => ({
          newsId: item.NewsID,
          title: item.Title,
          details: item.Details,
          urlLink: item.UrlLink,
          contentType: item.ContentType,
          createdAt: item.CreatedAt,
          imageSrc: item.imageSrc,
        }))
      )
    );
  }

  /**
   * Get all active news items for all orgs. SuperAdmin only.
   */
  getAllActiveNews(): Observable<NewsManagementItem[]> {
    return this.apiService.get<NewsManagementApiResponse>(`/v1/news/active`).pipe(
      unwrapApiEnvelope({ array: true, fallbackMessage: $localize`The news list was not in the expected format.` }),
      map((data) =>
        data.map(({ NewsID, Title, Details, UrlLink, ContentType, CreatedAt, VisibilityMode, VisibleToOrgs }) => {
          return {
            id: String(NewsID),
            title: Title,
            details: Details,
            urlLink: UrlLink,
            imageContentType: ContentType,
            createdAt: CreatedAt,
            visibility: VisibilityMode,
            visibleToOrgs: VisibleToOrgs.map(({ ID, Name }) => ({ id: String(ID), name: Name })),
          };
        })
      )
    );
  }

  getArchivedNews(limit = 50, offset = 0): Observable<NewsManagementArchivedItem[]> {
    const params = new HttpParams().set('limit', limit).set('offset', offset);
    return this.apiService.get<ArchivedNewsApiResponse>(`/v1/news/archived`, { params }).pipe(
      unwrapApiEnvelope({
        array: true,
        fallbackMessage: $localize`The archived news list was not in the expected format.`,
      }),
      map((data) =>
        data.map(
          // need visibility mode here?
          ({ NewsID, Title, CreatedAt, ArchivedAt, VisibilityMode }) => {
            return {
              id: String(NewsID),
              title: Title,
              createdAt: CreatedAt,
              archivedAt: ArchivedAt,
              visibility: VisibilityMode,
            };
          }
        )
      )
    );
  }

  /**
   * Creates news item, body is multipart/form-data.
   *
   * @param formData
   * @returns
   */
  createNews(formData: FormData): Observable<CreateNewsResponse> {
    return this.apiService.postFormData<CreateNewsResponse>(`/v1/news`, formData);
  }

  /**
   * For title, details, urlLink only
   */
  updateNewsMetadata(id: string, payload: UpdateNewsMetadataPayload): Observable<NewsActionResponse> {
    return this.apiService.patch<NewsActionResponse>(`/v1/news/${id}`, payload);
  }

  /**
   * Send new image file, as multipart/form-data
   */
  updateNewsImage(id: string, formData: FormData): Observable<NewsActionResponse> {
    return this.apiService.patchFormData<NewsActionResponse>(`/v1/news/${id}/image`, formData);
  }

  /**
   * Update visibility mode of news item.
   * Visibility mode is 'all' or 'selected'. If 'selected', number[] of org ids is required
   */
  updateNewsVisibility(id: string, payload: UpdateVisibilityPayload): Observable<NewsActionResponse> {
    return this.apiService.patch<NewsActionResponse>(`/v1/news/${id}/visibility`, payload);
  }

  /**
   * Archive news item. Visibility to orgs data is deleted.
   */
  archiveNews(id: string): Observable<NewsActionResponse> {
    return this.apiService.delete<NewsActionResponse>(`/v1/news/${id}`);
  }

  /**
   * Un-archive news item. Visibility will be 'all' regardless of previous.
   */
  unarchiveNews(id: string): Observable<NewsActionResponse> {
    return this.apiService.patch<NewsActionResponse>(`/v1/news/unarchive/${id}`, {});
  }

  /**
   * Permanently delete news item.
   */
  deleteNews(id: string): Observable<NewsActionResponse> {
    const params = new HttpParams({ fromObject: { hard: true } });
    return this.apiService.delete<NewsActionResponse>(`/v1/news/${id}`, { params });
  }
}
